import { useState, useEffect, useMemo } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  increment
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../context/AuthContext';
import { deleteDocumentComplete } from '../services/cloudFunctions';

export function useDocuments(projectId) {
  const [firestoreDocuments, setFirestoreDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [optimisticDeletes, setOptimisticDeletes] = useState(new Set());
  const [deletingDocuments, setDeletingDocuments] = useState(new Set());
  const { currentUser } = useAuth();

  // Filter out optimistically deleted documents
  const documents = useMemo(() => {
    return firestoreDocuments.filter(doc => !optimisticDeletes.has(doc.id));
  }, [firestoreDocuments, optimisticDeletes]);

  useEffect(() => {
    if (!currentUser || !projectId) {
      setFirestoreDocuments([]);
      setLoading(false);
      return;
    }

    // Create query for documents in this project
    const q = query(
      collection(db, 'documents'),
      where('userId', '==', currentUser.uid),
      where('projectId', '==', projectId),
      orderBy('createdAt', 'desc')
    );

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const documentsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setFirestoreDocuments(documentsData);
        setLoading(false);
        setError(null);

        // Cleanup: remove confirmed deletes from optimisticDeletes
        setOptimisticDeletes(prev => {
          const existingIds = new Set(documentsData.map(d => d.id));
          const next = new Set([...prev].filter(id => existingIds.has(id)));
          return next.size !== prev.size ? next : prev;
        });
      },
      (err) => {
        console.error('Error fetching documents:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser, projectId]);

  // Create a new document record
  const createDocument = async (documentData) => {
    try {
      if (!currentUser) throw new Error('User not authenticated');

      const docRef = await addDoc(collection(db, 'documents'), {
        ...documentData,
        userId: currentUser.uid,
        status: 'uploaded',
        piiItemCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Increment project document count
      if (documentData.projectId) {
        const projectRef = doc(db, 'projects', documentData.projectId);
        await updateDoc(projectRef, {
          documentCount: increment(1),
          updatedAt: serverTimestamp(),
        });
      }

      return { success: true, id: docRef.id };
    } catch (err) {
      console.error('Error creating document:', err);
      return { success: false, error: err.message };
    }
  };

  // Update a document
  const updateDocument = async (documentId, updates) => {
    try {
      if (!currentUser) throw new Error('User not authenticated');

      const documentRef = doc(db, 'documents', documentId);
      await updateDoc(documentRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });

      return { success: true };
    } catch (err) {
      console.error('Error updating document:', err);
      return { success: false, error: err.message };
    }
  };

  // Delete a document with optimistic updates
  const deleteDocument = async (documentId) => {
    try {
      if (!currentUser) throw new Error('User not authenticated');

      // Optimistic update: immediately hide from UI
      setOptimisticDeletes(prev => new Set([...prev, documentId]));
      setDeletingDocuments(prev => new Set([...prev, documentId]));

      // Call Cloud Function (handles Storage + Firestore + PII cleanup)
      const result = await deleteDocumentComplete(documentId);

      if (!result.success) {
        // Rollback on error
        setOptimisticDeletes(prev => {
          const next = new Set(prev);
          next.delete(documentId);
          return next;
        });
        return { success: false, error: result.error };
      }

      // Success - Firestore listener will handle final cleanup
      return { success: true };
    } catch (err) {
      console.error('Error deleting document:', err);

      // Rollback on exception
      setOptimisticDeletes(prev => {
        const next = new Set(prev);
        next.delete(documentId);
        return next;
      });

      return { success: false, error: err.message };
    } finally {
      // Clear from in-flight tracking
      setDeletingDocuments(prev => {
        const next = new Set(prev);
        next.delete(documentId);
        return next;
      });
    }
  };

  return {
    documents,
    loading,
    error,
    createDocument,
    updateDocument,
    deleteDocument,
    deletingDocuments,
  };
}
